import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import CategoryFields from "../CreateListing/CategoryFields";
import { categoryVisualMap } from "../CreateListing/categoryVisualMap";
import { compressImage } from "../../utils/imageCompress";
import "../CreateListing/CreateListing.scss";

import DynamicHeader from "../Header/DynamicHeader";
import Alerts from "../Alerts/Alerts";

const MAX_PHOTOS = 10;

const EditListing = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();

  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [showCategories, setShowCategories] = useState(false);

  const [photos, setPhotos] = useState([]);
  const [formData, setFormData] = useState({ attributes: {} });

  const [transferType, setTransferType] = useState("");
  const [priceData, setPriceData] = useState({});
  const [location, setLocation] = useState("");
  const [createdAt, setCreatedAt] = useState(null);

  const [uploading, setUploading] = useState(false);

  // Торг
  const [isNegotiable, setIsNegotiable] = useState(false);

  // Dirty + Alerts
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);

  const pendingLeaveRef = useRef(null);
  const pendingHrefRef = useRef(null);
  const pendingHistoryBackRef = useRef(false);

  const allowPopRef = useRef(false);
  const lastUrlRef = useRef(
    window.location.pathname + window.location.search + window.location.hash
  );

  const fileInputRef = useRef(null);
  const selectRef = useRef(null);
  const categoryTriggerRef = useRef(null);
  const photosGridRef = useRef(null);
  const titleRef = useRef(null);
  const priceRef = useRef(null);
  const exchangeRef = useRef(null);

  const navHistory = window.history;

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showCategories) return;
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setShowCategories(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setShowCategories(false);
    };
    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showCategories]);

  const buildSnapshot = useCallback(
    (override = {}) => {
      const tt = override.transferType ?? transferType ?? "";
      const pd = override.priceData ?? priceData ?? {};
      const attrs =
        override.attributes ??
        override.formDataAttributes ??
        (override.formData?.attributes ?? formData?.attributes ?? {});

      const normalized = {
        createdAt: override.createdAt ?? createdAt ?? null,

        title: override.title ?? title ?? "",
        description: override.description ?? description ?? "",
        category: override.category ?? category ?? "",

        photos: Array.isArray(override.photos ?? photos)
          ? [...(override.photos ?? photos)]
          : [],

        transferType: tt,

        priceData: {
          price: pd?.price ?? "",
          currency: pd?.currency ?? "",
          exchangeFor: pd?.exchangeFor ?? "",
        },

        location: override.location ?? location ?? "",

        attributes: attrs && typeof attrs === "object" ? attrs : {},

        isNegotiable:
          tt === "sale" ? !!(override.isNegotiable ?? isNegotiable) : false,
      };

      // стабілізуємо число
      if (
        normalized.priceData.price !== "" &&
        normalized.priceData.price != null
      ) {
        const n = Number(normalized.priceData.price);
        normalized.priceData.price = Number.isFinite(n)
          ? String(n)
          : String(normalized.priceData.price);
      } else {
        normalized.priceData.price = "";
      }

      normalized.priceData.currency = normalized.priceData.currency || "";
      normalized.priceData.exchangeFor = normalized.priceData.exchangeFor || "";

      normalized.title = String(normalized.title);
      normalized.description = String(normalized.description);
      normalized.category = String(normalized.category);
      normalized.location = String(normalized.location);

      return normalized;
    },
    [
      transferType,
      priceData,
      createdAt,
      title,
      description,
      category,
      photos,
      location,
      formData,
      isNegotiable,
    ]
  );

  const buildSnapshotFromFetched = useCallback((data) => {
    const nextTitle = data?.title || "";
    const nextDescription = data?.description || "";
    const nextCategory = data?.category || "";
    const nextPhotos = data?.photos || (data?.mainPhoto ? [data.mainPhoto] : []);
    const nextAttrs = data?.attributes || {};
    const nextTransfer = data?.type || "";
    const nextPriceData = {
      price: data?.price || "",
      currency: data?.currency || "",
      exchangeFor: data?.exchangeFor || "",
    };
    const nextLocation = data?.location || "";
    const nextCreatedAt = data?.createdAt || null;
    const nextNegotiable =
      nextTransfer === "sale" ? !!data?.isNegotiable : false;

    let priceStr = "";
    if (nextPriceData.price !== "" && nextPriceData.price != null) {
      const n = Number(nextPriceData.price);
      priceStr = Number.isFinite(n) ? String(n) : String(nextPriceData.price);
    }

    return {
      createdAt: nextCreatedAt,
      title: String(nextTitle),
      description: String(nextDescription),
      category: String(nextCategory),
      photos: Array.isArray(nextPhotos) ? [...nextPhotos] : [],
      transferType: String(nextTransfer),
      priceData: {
        price: priceStr,
        currency: nextPriceData.currency || "",
        exchangeFor: nextPriceData.exchangeFor || "",
      },
      location: String(nextLocation),
      attributes: nextAttrs && typeof nextAttrs === "object" ? nextAttrs : {},
      isNegotiable: nextTransfer === "sale" ? nextNegotiable : false,
    };
  }, []);

  const stableStringify = useCallback((obj) => {
    const seen = new WeakSet();
    const sorter = (value) => {
      if (value === null || typeof value !== "object") return value;
      if (seen.has(value)) return value;
      seen.add(value);

      if (Array.isArray(value)) return value.map(sorter);

      const keys = Object.keys(value).sort();
      const out = {};
      keys.forEach((k) => (out[k] = sorter(value[k])));
      return out;
    };

    try {
      return JSON.stringify(sorter(obj));
    } catch (e) {
      return "";
    }
  }, []);

  const currentSnapshot = useMemo(() => buildSnapshot(), [buildSnapshot]);

  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;
    return stableStringify(initialSnapshot) !== stableStringify(currentSnapshot);
  }, [initialSnapshot, currentSnapshot, stableStringify]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const onDocClickCapture = (e) => {
      if (!isDirty) return;

      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const a = e.target?.closest?.("a[href]");
      if (!a) return;

      const href = a.getAttribute("href");
      if (!href) return;

      const target = a.getAttribute("target");
      if (target && target !== "_self") return;
      if (a.hasAttribute("download")) return;
      if (href.startsWith("mailto:") || href.startsWith("tel:")) return;

      if (href.startsWith("http://") || href.startsWith("https://")) {
        try {
          const u = new URL(href, window.location.origin);
          if (u.origin !== window.location.origin) return;
        } catch (err) {
          return;
        }
      }

      const resolved = new URL(href, window.location.origin);
      const to = resolved.pathname + resolved.search + resolved.hash;
      const from =
        window.location.pathname + window.location.search + window.location.hash;

      if (to === from) return;

      e.preventDefault();
      e.stopPropagation();

      pendingHrefRef.current = to;
      pendingLeaveRef.current = null;
      pendingHistoryBackRef.current = false;

      setShowUnsavedAlert(true);
    };

    document.addEventListener("click", onDocClickCapture, true);
    return () => document.removeEventListener("click", onDocClickCapture, true);
  }, [isDirty]);

  useEffect(() => {
    const onPopState = () => {
      const currentUrl =
        window.location.pathname + window.location.search + window.location.hash;

      if (allowPopRef.current) {
        allowPopRef.current = false;
        lastUrlRef.current = currentUrl;
        return;
      }

      if (!isDirty) {
        lastUrlRef.current = currentUrl;
        return;
      }

      try {
        navHistory.pushState(null, "", lastUrlRef.current);
      } catch (e) {}

      pendingHistoryBackRef.current = true;
      pendingLeaveRef.current = null;
      pendingHrefRef.current = null;

      setShowUnsavedAlert(true);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isDirty, navHistory]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`/api/listings/${listingId}`);
        if (!res.data) {
          setMessage("Оголошення не знайдено");
          return;
        }
        if (userId && res.data.userId && userId !== res.data.userId) {
          setMessage("У вас немає прав на редагування цього оголошення");
          return;
        }

        setTitle(res.data.title || "");
        setDescription(res.data.description || "");
        setCategory(res.data.category || "");
        setPhotos(
          res.data.photos || (res.data.mainPhoto ? [res.data.mainPhoto] : [])
        );
        setFormData({ attributes: res.data.attributes || {} });
        setTransferType(res.data.type || "");
        setPriceData({
          price: res.data.price || "",
          currency: res.data.currency || "",
          exchangeFor: res.data.exchangeFor || "",
        });
        setLocation(res.data.location || "");
        setCreatedAt(res.data.createdAt || null);

        setIsNegotiable(
          (res.data.type || "") === "sale" ? !!res.data.isNegotiable : false
        );

        const snap = buildSnapshotFromFetched(res.data);
        setInitialSnapshot(snap);

        lastUrlRef.current =
          window.location.pathname +
          window.location.search +
          window.location.hash;
      } catch (e) {
        console.error("load listing:", e);
        setMessage("Не вдалося завантажити оголошення");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [listingId, userId, buildSnapshotFromFetched]);

  const uploadSingleFile = async (file) => {
    const compressed = await compressImage(file, {
      maxSide: 1600,
      mimeType: "image/webp",
      quality: 0.82,
    });
    const fd = new FormData();
    fd.append("photo", compressed, compressed.name);
    const res = await axios.post("/api/listings/upload-photo", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.photoUrl;
  };

  const handlePhotosSelect = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    const toUpload = fileList.slice(0, remaining);
    try {
      setUploading(true);
      const uploaded = [];
      for (const f of toUpload) {
        try {
          uploaded.push(await uploadSingleFile(f));
        } catch (err) {
          console.error("file upload:", err);
        }
      }
      if (uploaded.length) setPhotos((prev) => [...prev, ...uploaded]);
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = "";
    }
  };

  const removePhotoAt = (index) =>
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  const triggerFileDialog = () => fileInputRef.current?.click();

  const dragItemIndex = useRef(null);
  const onDragStart = (index) => (e) => {
    dragItemIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.classList.add("dragging");
  };
  const onDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    e.currentTarget.classList.add("drag-over");
  };
  const onDrop = (index) => (e) => {
    e.preventDefault();
    const from = dragItemIndex.current;
    if (from === null || from === index) {
      e.currentTarget.classList.remove("drag-over");
      return;
    }
    setPhotos((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(index, 0, moved);
      return arr;
    });
    dragItemIndex.current = null;
    e.currentTarget.classList.remove("drag-over");
    document
      .querySelectorAll(".photo-grid .tile.filled.dragging")
      .forEach((el) => el.classList.remove("dragging"));
  };

  const scrollToAndMark = (ref, className = "field-error") => {
    const node = ref?.current ?? ref;
    if (!node) return;
    node.classList.add(className);
    if (node.scrollIntoView)
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    const inputEl = node.querySelector?.("input, textarea, select") || node;
    inputEl.focus?.();
    setTimeout(() => node.classList.remove(className), 2400);
  };

  const playfulLines = {
    title: "Заголовок обовʼязковий.",
    category: "Оберіть категорію, щоб зберегти зміни.",
    price: "Вкажіть суму або змініть тип передачі.",
    exchange: "Вкажіть, на що міняєте.",
    createdAt: "Відсутній ключ createdAt. Неможливо зберегти.",
  };

  const setDeal = (type) => {
    if (type === "sale") {
      setTransferType("sale");
      return;
    }
    if (type === "exchange") {
      setTransferType("exchange");
      setIsNegotiable(false);
      return;
    }
    setTransferType("free");
    setIsNegotiable(false);
  };

  const saveListing = async () => {
    if (!createdAt) {
      setMessage(playfulLines.createdAt);
      return false;
    }
    if (!title.trim()) {
      setMessage(playfulLines.title);
      scrollToAndMark(titleRef);
      return false;
    }
    if (!category) {
      setMessage(playfulLines.category);
      scrollToAndMark(categoryTriggerRef);
      return false;
    }

    if (transferType === "sale" && !priceData.price) {
      setMessage(playfulLines.price);
      scrollToAndMark(priceRef);
      return false;
    }
    if (transferType === "exchange" && !priceData.exchangeFor) {
      setMessage(playfulLines.exchange);
      scrollToAndMark(exchangeRef);
      return false;
    }

    const payload = {
      createdAt,
      title,
      description,
      category,
      photos,
      price:
        transferType === "sale"
          ? parseFloat(priceData.price || 0)
          : transferType === "free"
          ? 0
          : priceData.price || 0,
      type: transferType || "",
      currency: priceData.currency || "",
      exchangeFor: priceData.exchangeFor || "",
      location: location || "",
      attributes: formData.attributes || {},

      isNegotiable: transferType === "sale" ? !!isNegotiable : false,
    };

    try {
      const token = await getAccessToken();

      if (!token) {
        setMessage("Сесія недійсна. Увійдіть повторно.");
        return false;
      }

      await axios.patch(`/api/listings/${listingId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMessage("Зміни збережено");

      const newSnap = buildSnapshot();
      setInitialSnapshot(newSnap);

      return true;
    } catch (err) {
      console.error("save listing:", err);
      const m = err?.response?.data?.error || "Не вдалося зберегти зміни";
      setMessage(`${m}`);
      return false;
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const ok = await saveListing();
    if (!ok) return;
    setTimeout(() => navigate(-1), 600);
  };

  const attemptLeave = useCallback(
    (leaveFn) => {
      if (!isDirty) {
        leaveFn();
        return;
      }
      pendingLeaveRef.current = leaveFn;
      pendingHrefRef.current = null;
      pendingHistoryBackRef.current = false;
      setShowUnsavedAlert(true);
    },
    [isDirty]
  );

  const clearLeaveContext = () => {
    pendingLeaveRef.current = null;
    pendingHrefRef.current = null;
    pendingHistoryBackRef.current = false;
  };

  const closeUnsavedAlert = () => {
    setShowUnsavedAlert(false);
    clearLeaveContext();
  };

  const proceedWithoutSaving = () => {
    setShowUnsavedAlert(false);

    // 1) pending link
    if (pendingHrefRef.current) {
      const to = pendingHrefRef.current;
      clearLeaveContext();
      navigate(to);
      return;
    }

    // 2) browser back
    if (pendingHistoryBackRef.current) {
      clearLeaveContext();
      allowPopRef.current = true;
      navHistory.back();
      return;
    }

    // 3) manual leave
    if (typeof pendingLeaveRef.current === "function") {
      const fn = pendingLeaveRef.current;
      clearLeaveContext();
      fn();
      return;
    }

    clearLeaveContext();
  };

  const saveThenLeave = async () => {
    const ok = await saveListing();
    if (!ok) return;

    setShowUnsavedAlert(false);

    if (pendingHrefRef.current) {
      const to = pendingHrefRef.current;
      clearLeaveContext();
      navigate(to);
      return;
    }

    if (pendingHistoryBackRef.current) {
      clearLeaveContext();
      allowPopRef.current = true;
      navHistory.back();
      return;
    }

    if (typeof pendingLeaveRef.current === "function") {
      const fn = pendingLeaveRef.current;
      clearLeaveContext();
      fn();
      return;
    }

    clearLeaveContext();
    setTimeout(() => navigate(-1), 250);
  };

  if (loading) {
    return (
      <>
        <DynamicHeader
          title="Редагувати оголошення"
          variant="glass"
          size="compact"
          hideOnScroll
          showBack
          onBack={() => attemptLeave(() => navigate(-1))}
          showMenu={false}
          rightNode={null}
          withSpacer={false}
        />

        <div className="local-header-spacer small" />

        <div
          className="create-listing edit-listing skeleton"
          aria-busy="true"
          aria-label="Завантаження форми редагування оголошення"
        >
          <div className="sk-line sk-title sk-shimmer" />
          <div className="sk-block sk-input sk-shimmer" />
          <div className="sk-block sk-textarea sk-shimmer" />
          <div className="sk-block sk-category sk-shimmer" />
          <div className="sk-block sk-photos sk-shimmer" />
          <div className="sk-block sk-transfer sk-shimmer" />
          <div className="sk-block sk-location sk-shimmer" />
          <div className="sk-block sk-button sk-shimmer" />
        </div>
      </>
    );
  }

  return (
    <>
      <DynamicHeader
        title="Редагувати оголошення"
        variant="glass"
        size="compact"
        hideOnScroll
        showBack
        onBack={() => attemptLeave(() => navigate(-1))}
        showMenu={false}
        rightNode={null}
        withSpacer={false}
      />

      <Alerts
        open={showUnsavedAlert}
        variant="unsavedChanges"
        onClose={closeUnsavedAlert}
        onCancel={closeUnsavedAlert}
        onSecondary={proceedWithoutSaving}
        onPrimary={saveThenLeave}
      />

      <div className="local-header-spacer small" />

      <div
        className={`create-listing edit-listing ${mounted ? "enter" : ""}`}
        role="region"
        aria-label="Редагування оголошення"
      >
        {message && <p className="message">{message}</p>}

        <form onSubmit={handleSave} className="create-listing-form" noValidate>
          <h3>Деталі оголошення</h3>

          <input
            ref={titleRef}
            type="text"
            className="pill-50"
            placeholder="Заголовок *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-required="true"
          />

          <textarea
            className="pill-50"
            placeholder="Опис"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="custom-select-wrapper" ref={selectRef}>
            <div
              ref={categoryTriggerRef}
              className={`custom-select-trigger pill-50 ${
                showCategories ? "open" : ""
              }`}
              onClick={() => setShowCategories(!showCategories)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") &&
                setShowCategories(!showCategories)
              }
              aria-haspopup="listbox"
              aria-expanded={showCategories}
              aria-controls="category-dropdown"
            >
              {category ? (
                <div className="selected-category">
                  <div
                    className="category-icon"
                    style={{
                      backgroundImage: `url(${categoryVisualMap[category]})`,
                    }}
                  />
                  <span>{category}</span>
                </div>
              ) : (
                <span className="placeholder">Оберіть категорію *</span>
              )}
              <span className="chevron" aria-hidden="true">
                ▾
              </span>
            </div>

            <div
              id="category-dropdown"
              className={`custom-select-dropdown ${
                showCategories ? "open" : "closed"
              }`}
              role="listbox"
              aria-hidden={!showCategories}
            >
              {Object.entries(categoryVisualMap).map(([name, image], idx) => (
                <div
                  key={name}
                  className="dropdown-item"
                  style={{ "--item-index": idx }}
                  onClick={() => {
                    setCategory(name);
                    setShowCategories(false);
                  }}
                  role="option"
                  aria-selected={category === name}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setCategory(name);
                      setShowCategories(false);
                    }
                  }}
                >
                  <div
                    className="category-icon"
                    style={{ backgroundImage: `url(${image})` }}
                  />
                  <span>{name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="photo-upload">
            <label>Фото</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotosSelect}
              style={{ display: "none" }}
              disabled={uploading}
            />
          </div>

          <div className="photo-grid" aria-label="Сітка фото" ref={photosGridRef}>
            {Array.from({
              length: Math.min(
                MAX_PHOTOS,
                Math.max(MAX_PHOTOS, photos.length + 1)
              ),
            }).map((_, idx) => {
              const url = photos[idx];
              if (url) {
                return (
                  <div
                    key={`tile-${idx}`}
                    className="tile filled"
                    draggable
                    onDragStart={onDragStart(idx)}
                    onDragOver={onDragOver(idx)}
                    onDrop={onDrop(idx)}
                  >
                    <img src={url} alt={`Фото ${idx + 1}`} />
                    {idx === 0 && <span className="main-badge">Головне</span>}
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removePhotoAt(idx)}
                      aria-label="Видалити фото"
                      title="Видалити фото"
                      disabled={uploading}
                    >
                      ×
                    </button>
                  </div>
                );
              }

              if (idx === photos.length && photos.length < MAX_PHOTOS) {
                return (
                  <button
                    key={`tile-empty-${idx}`}
                    type="button"
                    className="tile empty add-more"
                    onClick={triggerFileDialog}
                    disabled={uploading}
                    aria-label="Додати фото"
                    title="Додати фото"
                  >
                    <span className="plus">+</span>
                  </button>
                );
              }
              return null;
            })}
          </div>

          {category && (
            <CategoryFields
              category={category}
              formData={formData}
              setFormData={setFormData}
            />
          )}

          <div className="transfer-type-toggle">
            <h3>Умови пропозиції</h3>
            <div className="toggle-buttons segmented glassy">
              <button
                type="button"
                className={transferType === "sale" ? "active" : ""}
                onClick={() => setDeal("sale")}
              >
                Платно
              </button>
              <button
                type="button"
                className={transferType === "exchange" ? "active" : ""}
                onClick={() => setDeal("exchange")}
              >
                Обмін
              </button>
              <button
                type="button"
                className={transferType === "free" ? "active" : ""}
                onClick={() => setDeal("free")}
              >
                Безкоштовно
              </button>
            </div>

            {transferType === "sale" && (
              <>
                <div className="price-fields">
                  <input
                    ref={priceRef}
                    type="number"
                    className="pill-50"
                    placeholder="Сума *"
                    value={priceData.price || ""}
                    onChange={(e) =>
                      setPriceData({
                        ...priceData,
                        price: e.target.value,
                      })
                    }
                  />
                  <select
                    value={priceData.currency || "UAH"}
                    onChange={(e) =>
                      setPriceData({
                        ...priceData,
                        currency: e.target.value,
                      })
                    }
                    aria-label="Валюта"
                  >
                    <option value="">Валюта</option>
                    <option value="UAH">₴</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                  </select>
                </div>

                <div className="negotiable-block ios-glass-block">
                  <div className="toggle-row">
                    <div className="toggle-text">
                      <div className="title">Можливий торг</div>
                      <div className={`state ${isNegotiable ? "on" : "off"}`}>
                        {isNegotiable ? "УВІМКНЕНО" : "ВИМКНЕНО"}
                      </div>
                    </div>

                    <label
                      className={`pretty-switch ${
                        isNegotiable ? "active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isNegotiable}
                        onChange={(e) => setIsNegotiable(e.target.checked)}
                        aria-label="Можливий торг"
                      />
                      <span className="track">
                        <span className="thumb" />
                      </span>
                    </label>
                  </div>
                </div>
              </>
            )}

            {transferType === "exchange" && (
              <input
                ref={exchangeRef}
                type="text"
                className="pill-50"
                placeholder="На що хочете обміняти?"
                value={priceData.exchangeFor || ""}
                onChange={(e) =>
                  setPriceData({
                    ...priceData,
                    exchangeFor: e.target.value,
                  })
                }
              />
            )}

            {transferType === "free" && (
              <p className="free-note">Люди люблять безкоштовне — ви молодець!</p>
            )}
          </div>

          <h3>Контактна інформація</h3>
          <input
            type="text"
            className="pill-50"
            placeholder="Локація (необов’язково)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />

          <div className="cta-sticky">
            <button
              type="submit"
              disabled={uploading || !isDirty}
              className={`primary-cta with-sheen ${
                !isDirty || uploading ? "is-disabled" : ""
              }`}
              aria-disabled={uploading || !isDirty}
              title={!isDirty ? "Немає змін для збереження" : ""}
            >
              {uploading ? "Збереження..." : "Зберегти зміни"}
            </button>
          </div>

          <div className="bottom-safe-space" aria-hidden="true" />
        </form>
      </div>
    </>
  );
};

export default EditListing;